<template>
  <div class="hs-line-chart">
    <Line :data="computedChartData" :options="chartOptions" :plugins="gradientPlugin" />
  </div>
</template>

<script>
import { Line } from "vue-chartjs";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

function hexToRgba(hex, alpha) {
  const safeHex = String(hex || "#3293ec").replace("#", "");
  const r = parseInt(safeHex.substring(0, 2), 16);
  const g = parseInt(safeHex.substring(2, 4), 16);
  const b = parseInt(safeHex.substring(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

export default {
  name: "HsLineChart",
  components: { Line },
  props: {
    labels: { type: Array, default: () => [] },
    datasets: { type: Array, default: () => [] },
    colors: { type: Array, default: () => ["#3293ec", "#16a34a", "#f59e0b"] },
    yTitle: { type: String, default: "" },
    xTitle: { type: String, default: "" },
    currency: { type: String, default: "" },
  },
  data() {
    return {
      gradientPlugin: [
        {
          id: "hsLineGradient",
          beforeDraw(chart) {
            const { ctx, chartArea, data } = chart;
            if (!chartArea) return;
            data.datasets.forEach((dataset) => {
              if (!dataset.fill || typeof dataset.borderColor !== "string") return;
              const hex = dataset.borderColor.replace("#", "");
              const r = parseInt(hex.substring(0, 2), 16);
              const g = parseInt(hex.substring(2, 4), 16);
              const b = parseInt(hex.substring(4, 6), 16);
              const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
              gradient.addColorStop(0, `rgba(${r},${g},${b},0.24)`);
              gradient.addColorStop(1, `rgba(${r},${g},${b},0)`);
              dataset.backgroundColor = gradient;
            });
          },
        },
      ],
    };
  },
  computed: {
    computedChartData() {
      const datasets = this.datasets.map((dataset, index) => {
        const color = dataset.color || this.colors[index % this.colors.length] || "#3293ec";
        return {
          label: dataset.label || dataset.name || `Series ${index + 1}`,
          data: dataset.data || [],
          borderColor: color,
          backgroundColor: hexToRgba(color, 0.13),
          fill: dataset.fill !== false,
          tension: 0.4,
          borderWidth: 2.5,
          pointRadius: 0,
          pointHoverRadius: 5,
          pointHoverBackgroundColor: "#fff",
          pointHoverBorderColor: color,
          pointHoverBorderWidth: 2.5,
        };
      });
      return { labels: this.labels, datasets };
    },
    chartOptions() {
      const isDark = document.documentElement.classList.contains("dark");
      const tickColor = isDark ? "rgba(255,255,255,0.48)" : "rgba(15,23,42,0.55)";
      const gridColor = isDark ? "rgba(255,255,255,0.07)" : "rgba(15,23,42,0.06)";
      const currency = this.currency;
      return {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        animation: { duration: 700, easing: "easeInOutQuart" },
        plugins: {
          legend: {
            display: this.datasets.length > 1,
            labels: { color: tickColor, usePointStyle: true, boxWidth: 8 },
          },
          tooltip: {
            backgroundColor: isDark ? "#1e293b" : "#0f172a",
            titleColor: "#94a3b8",
            bodyColor: "#f8fafc",
            borderColor: "rgba(255,255,255,0.1)",
            borderWidth: 1,
            cornerRadius: 12,
            displayColors: false,
            padding: { x: 14, y: 10 },
            callbacks: {
              label(ctx) {
                const value = Number(ctx.parsed.y || 0).toLocaleString();
                return ` ${currency ? `${currency} ` : ""}${value}`;
              },
            },
          },
        },
        scales: {
          x: {
            grid: { display: false },
            border: { display: false },
            ticks: { color: tickColor, maxTicksLimit: 8, maxRotation: 0 },
            title: this.xTitle ? { display: true, text: this.xTitle, color: tickColor } : { display: false },
          },
          y: {
            grid: { color: gridColor },
            border: { display: false },
            ticks: {
              color: tickColor,
              maxTicksLimit: 6,
              callback(value) {
                if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
                if (value >= 1_000) return `${(value / 1_000).toFixed(1).replace(/\.0$/, "")}K`;
                return value;
              },
            },
            title: this.yTitle ? { display: true, text: this.yTitle, color: tickColor } : { display: false },
          },
        },
      };
    },
  },
};
</script>

<style scoped>
.hs-line-chart {
  height: 100%;
  min-height: 280px;
  width: 100%;
}
</style>
